
import { useQC } from './hooks/useQC';
import { useState } from 'react';
import type { QCChecklist, QCChecklistItem } from './types';

export default function ChecklistManager() {
  const { checklists, saveChecklist, deleteChecklist } = useQC();
  const [isEditing, setIsEditing] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState<Partial<QCChecklist> | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  const handleEdit = (checklist?: QCChecklist) => {
    if (checklist) {
      setEditingChecklist(checklist);
    } else {
      setEditingChecklist({
        name: '',
        description: '',
        category: 'general',
        isActive: true,
        items: []
      });
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!editingChecklist || !editingChecklist.name?.trim()) {
      alert('Please provide a checklist name.');
      return;
    }
    
    if (!editingChecklist.items || editingChecklist.items.length === 0) {
      alert('Please add at least one checklist item.');
      return;
    }
    
    try {
      await saveChecklist.mutateAsync(editingChecklist);
      setIsEditing(false);
      setEditingChecklist(null);
    } catch (error) {
      console.error('Failed to save checklist:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteChecklist.mutateAsync(id);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete checklist:', error);
    }
  };

  const addChecklistItem = () => {
    if (!editingChecklist) return;
    
    const newItem = {
      id: Date.now().toString(),
      description: '',
      category: '',
      isRequired: false
    };
    
    setEditingChecklist({
      ...editingChecklist,
      items: [...(editingChecklist.items || []), newItem]
    });
  };

  const updateChecklistItem = (index: number, updates: Partial<Omit<QCChecklistItem, 'status' | 'notes' | 'photos'>>) => {
    if (!editingChecklist?.items) return;
    
    const updatedItems = [...editingChecklist.items];
    updatedItems[index] = { ...updatedItems[index], ...updates };
    
    setEditingChecklist({
      ...editingChecklist,
      items: updatedItems
    });
  };

  const removeChecklistItem = (index: number) => {
    if (!editingChecklist?.items) return;
    
    const updatedItems = editingChecklist.items.filter((_, i) => i !== index);
    setEditingChecklist({
      ...editingChecklist,
      items: updatedItems
    });
  };

  if (checklists.isLoading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Checklist Management</h1>
        <button
          onClick={() => handleEdit()}
          className="btn-primary flex items-center"
        >
          <i className="ri-add-line mr-2"></i>
          Create New Checklist
        </button>
      </div>

      {/* Checklists Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {checklists.data?.map((checklist) => (
          <div key={checklist.id} className="card">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{checklist.name}</h3>
                <p className="text-sm text-gray-600 mt-1">{checklist.description}</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`badge ${checklist.isActive ? 'approved' : 'suspended'}`}>
                  {checklist.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Category:</span>
                <span className="font-medium capitalize">{checklist.category}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Items:</span>
                <span className="font-medium">{checklist.items.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Required Items:</span>
                <span className="font-medium">
                  {checklist.items.filter(item => item.isRequired).length}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Last Updated:</span>
                <span className="font-medium">
                  {new Date(checklist.updatedAt).toLocaleDateString()}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <button
                onClick={() => handleEdit(checklist)}
                className="text-[var(--color-primary)] hover:text-[var(--color-gold)] text-sm flex items-center"
              >
                <i className="ri-edit-line mr-1"></i>
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(checklist.id)}
                className="text-red-600 hover:text-red-800 text-sm flex items-center"
              >
                <i className="ri-delete-bin-line mr-1"></i>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {isEditing && editingChecklist && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold">
                {editingChecklist.id ? 'Edit Checklist' : 'Create New Checklist'}
              </h2>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Checklist Name *
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={editingChecklist.name || ''}
                    onChange={(e) => setEditingChecklist({
                      ...editingChecklist,
                      name: e.target.value
                    })}
                    placeholder="Enter checklist name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    className="input"
                    value={editingChecklist.category || 'general'}
                    onChange={(e) => setEditingChecklist({
                      ...editingChecklist,
                      category: e.target.value as any
                    })}
                  >
                    <option value="general">General</option>
                    <option value="gold">Gold Jewelry</option>
                    <option value="diamond">Diamond Jewelry</option>
                    <option value="silver">Silver Jewelry</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  className="input"
                  rows={3}
                  value={editingChecklist.description || ''}
                  onChange={(e) => setEditingChecklist({
                    ...editingChecklist,
                    description: e.target.value
                  })}
                  placeholder="Enter checklist description"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  className="mr-2"
                  checked={editingChecklist.isActive || false}
                  onChange={(e) => setEditingChecklist({
                    ...editingChecklist,
                    isActive: e.target.checked
                  })}
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                  Active Checklist
                </label>
              </div>

              {/* Checklist Items */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Checklist Items</h3>
                  <button
                    onClick={addChecklistItem}
                    className="btn-secondary flex items-center text-sm"
                  >
                    <i className="ri-add-line mr-1"></i>
                    Add Item
                  </button>
                </div>

                <div className="space-y-4">
                  {editingChecklist.items?.map((item, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="grid md:grid-cols-2 gap-4 mb-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description *
                          </label>
                          <input
                            type="text"
                            className="input"
                            value={item.description}
                            onChange={(e) => updateChecklistItem(index, {
                              description: e.target.value
                            })}
                            placeholder="Enter item description"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Category
                          </label>
                          <input
                            type="text"
                            className="input"
                            value={item.category}
                            onChange={(e) => updateChecklistItem(index, {
                              category: e.target.value
                            })}
                            placeholder="Enter item category"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id={`required-${index}`}
                            className="mr-2"
                            checked={item.isRequired}
                            onChange={(e) => updateChecklistItem(index, {
                              isRequired: e.target.checked
                            })}
                          />
                          <label htmlFor={`required-${index}`} className="text-sm text-gray-700">
                            Required Item
                          </label>
                        </div>
                        <button
                          onClick={() => removeChecklistItem(index)}
                          className="text-red-600 hover:text-red-800 text-sm flex items-center"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}

                  {(!editingChecklist.items || editingChecklist.items.length === 0) && (
                    <div className="text-center py-8 text-gray-500">
                      <i className="ri-file-list-line text-4xl mb-2"></i>
                      <div>No items added yet</div>
                      <div className="text-sm">Click "Add Item" to create checklist items</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex items-center justify-end space-x-4">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditingChecklist(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="btn-primary"
                disabled={saveChecklist.isPending || !editingChecklist.name}
              >
                {saveChecklist.isPending ? 'Saving...' : 'Save Checklist'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this checklist? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end space-x-4">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="btn-danger"
                disabled={deleteChecklist.isPending}
              >
                {deleteChecklist.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
